import './client-proposal-actions.scss'

import {
  useEffect,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { Check } from '@vedmarket/ui-kit'
import { useUser } from 'api/auth'

import ModalBig from '../../../../common/modalBig'
import ApplicationPhoneField from './application-phone-field'
import { validateClientContactsForm } from '../reveal-contacts-form'

const buildDefaultContactForm = (user: MeParamsOutput | null | undefined) => {
  const name = [ user?.first_name, user?.last_name ].filter(Boolean).join(' ').trim()
  return {
    contact_name: name,
    contact_phone: user?.phone?.trim() || '',
    contact_email: user?.email?.trim() || '',
    allow_executor_contact_exchange: false,
  }
}

const ProposalModalActions = ({
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  confirmColor,
  confirmDisabled = false,
}: {
  cancelLabel: string
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  confirmColor?: 'error' | 'primary' | 'inherit'
  confirmDisabled?: boolean
}) => (
  <div className='application-client-proposal-actions__modal-actions'>
    <Button
      variant='outlined'
      sx={{ minWidth: '238px', marginRight: 3, flexShrink: 0 }}
      onClick={onCancel}
    >
      <Typography component='span' variant='inherit'>
        {cancelLabel}
      </Typography>
    </Button>
    <Button
      variant='contained'
      fullWidth
      color={confirmColor}
      disabled={confirmDisabled}
      onClick={onConfirm}
    >
      <Typography component='span' variant='inherit'>
        {confirmLabel}
      </Typography>
    </Button>
  </div>
)

const ClientProposalActions = ({
  runAction,
  onDeclineProposal,
  onAcceptProposal,
}: ApplicationClientProposalActionsProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const [ declineOpen, setDeclineOpen ] = useState(false)
  const [ acceptOpen, setAcceptOpen ] = useState(false)
  const [ declineReason, setDeclineReason ] = useState('')
  const [ contactForm, setContactForm ] = useState(() => buildDefaultContactForm(user))
  const [ formError, setFormError ] = useState<string | null>(null)
  const [ emailTouched, setEmailTouched ] = useState(false)
  const contactErrors = validateClientContactsForm({
    form: {
      client_contact_name: contactForm.contact_name,
      client_contact_phone: contactForm.contact_phone,
      client_contact_email: contactForm.contact_email,
    },
    t,
  })

  useEffect(() => {
    if (!acceptOpen) return
    setContactForm(buildDefaultContactForm(user))
    setFormError(null)
    setEmailTouched(false)
  }, [ acceptOpen, user ])

  const handleDeclineConfirm = () => {
    setDeclineOpen(false)
    runAction(() => onDeclineProposal(declineReason), 'applications__message__proposalDeclined')
    setDeclineReason('')
  }

  const handleAcceptConfirm = () => {
    const name = contactForm.contact_name.trim()
    const phone = contactForm.contact_phone.trim()
    const email = contactForm.contact_email.trim()
    const errors = validateClientContactsForm({
      form: {
        client_contact_name: name,
        client_contact_phone: phone,
        client_contact_email: email,
      },
      t,
    })
    const firstError = Object.values(errors)[0]
    if (firstError) {
      setFormError(firstError)
      return
    }
    setFormError(null)
    setAcceptOpen(false)
    runAction(() => onAcceptProposal({
      contact_name: name,
      contact_phone: phone,
      contact_email: email,
      allow_executor_contact_exchange: contactForm.allow_executor_contact_exchange,
    }), 'applications__message__proposalAccepted')
  }

  return (
    <Box className='application-client-proposal-actions'>
      <Box
        className='application-client-proposal-actions__buttons'
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          justifyContent: 'space-between',
          gap: '24px',
        }}
      >
        <Button
          variant='outlined'
          color='error'
          onClick={() => setDeclineOpen(true)}
          sx={{
            minWidth: '238px',
            flexShrink: 0,
          }}
        >
          <Typography component='span' variant='inherit'>
            {t('applications__text__declineProposalShort')}
          </Typography>
        </Button>
        <Button
          variant='contained'
          onClick={() => setAcceptOpen(true)}
          startIcon={<Check />}
          sx={{
            flex: 1,
          }}
        >
          <Typography component='span' variant='inherit'>
            {t('applications__text__acceptProposal')}
          </Typography>
        </Button>
      </Box>

      <ModalBig
        isOpen={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title={t('applications__text__declineProposalModalTitle')}
        width={736}
        buttons={(
          <ProposalModalActions
            cancelLabel={t('actions__text__cancel')}
            confirmLabel={t('applications__text__declineProposalShort')}
            confirmColor='error'
            onCancel={() => setDeclineOpen(false)}
            onConfirm={handleDeclineConfirm}
          />
        )}
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={<Typography component='span' variant='inherit'>
            {t('applications__text__declineReason')}
          </Typography>}
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
        />
      </ModalBig>

      <ModalBig
        isOpen={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        title={t('applications__text__acceptProposalModalTitle')}
        width={736}
        buttons={(
          <ProposalModalActions
            cancelLabel={t('actions__text__cancel')}
            confirmLabel={t('applications__text__acceptProposal')}
            confirmDisabled={!contactForm.allow_executor_contact_exchange || Object.keys(contactErrors).length > 0}
            onCancel={() => setAcceptOpen(false)}
            onConfirm={handleAcceptConfirm}
          />
        )}
      >
        <Typography variant='body2' color='text.primary' sx={{ marginBottom: 2 }}>
          {t('applications__text__acceptProposalModalHint')}
        </Typography>
        <TextField
          fullWidth
          size='small'
          sx={{ marginBottom: 1.5 }}
          label={<Typography component='span' variant='inherit'>
            {t('applications__text__acceptProposalContactName')}
          </Typography>}
          value={contactForm.contact_name}
          onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
        />
        <Box sx={{ marginBottom: 1.5 }}>
          <ApplicationPhoneField
            label={t('applications__text__acceptProposalContactPhone')}
            value={contactForm.contact_phone}
            onChange={(value) => setContactForm({ ...contactForm, contact_phone: value })}
          />
        </Box>
        <TextField
          className='application-client-proposal-actions__contact-field'
          type='email'
          autoComplete='email'
          fullWidth
          size='small'
          sx={{ marginBottom: 1 }}
          label={<Typography component='span' variant='inherit'>
            {t('applications__text__acceptProposalContactEmail')}
          </Typography>}
          error={emailTouched && Boolean(contactErrors.client_contact_email)}
          helperText={emailTouched ? contactErrors.client_contact_email : undefined}
          onBlur={() => setEmailTouched(true)}
          value={contactForm.contact_email}
          onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
        />
        <FormControlLabel
          className='application-client-proposal-actions__contact-exchange'
          control={(
            <Checkbox
              checked={contactForm.allow_executor_contact_exchange}
              onChange={(e) => setContactForm({
                ...contactForm,
                allow_executor_contact_exchange: e.target.checked,
              })}
            />
          )}
          label={<Typography component='span' variant='body2'>
            {t('applications__text__acceptProposalAllowExecutorContacts')}
          </Typography>}
        />
        {formError && (
          <Typography variant='body2' color='error' sx={{ marginTop: 1 }}>
            {formError}
          </Typography>
        )}
      </ModalBig>
    </Box>
  )
}

export default ClientProposalActions
